import { useContext, useState } from "react";
import { X } from "lucide-react";
import { AuthContext } from "@/contexts/AuthContext";
import { getStripeEnvironment } from "@/lib/stripe";

/**
 * Small fixed badge showing which Stripe environment the current build is
 * talking to. Only rendered for admin users. Safely returns null when
 * mounted outside AuthProvider (e.g. on public routes).
 */
export function StripeModeIndicator() {
  const isDevBuild = import.meta.env.DEV;
  const auth = useContext(AuthContext);
  const [dismissed, setDismissed] = useState(false);
  const env = getStripeEnvironment();
  const isLive = env === "live";
  const token = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;
  const keyTail = token ? token.slice(-6) : "??????";

  if (dismissed) return null;
  // Dev-only debug badge: never rendered in production builds.
  if (!isDevBuild) return null;
  // Admin-only. If AuthProvider isn't mounted or user isn't admin, hide.
  if (!auth || auth.role !== "admin") return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`Stripe ${isLive ? "live" : "test"} mode, key ending ${keyTail}`}
      className={[
        // Sits above phone bottom navigation so it never covers tab buttons
        "fixed bottom-20 right-3 z-[60] flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium shadow-md backdrop-blur sm:bottom-3",
        isLive
          ? "border-emerald-500/60 bg-emerald-700 text-white"
          : "border-orange-500/60 bg-orange-700 text-white",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "inline-block h-1.5 w-1.5 rounded-full",
          isLive ? "bg-white" : "bg-white animate-pulse",
        ].join(" ")}
      />
      <span>Stripe: {isLive ? "LIVE" : "TEST"}</span>
      <span
        title={`Publishable key ends in ${keyTail}`}
        className="rounded bg-black/25 px-1.5 py-0.5 font-mono text-[10px] tracking-wide"
      >
        …{keyTail}
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss Stripe mode indicator"
        className="ml-1 rounded p-0.5 transition-colors hover:bg-white/20"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </div>
  );
}
